import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Alert,
  Box,
  Container,
  Grid,
  Paper,
  Typography,
  TextField,
  Autocomplete,
  CircularProgress,
  alpha
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import WaterDropIcon from '@mui/icons-material/WaterDrop';
import PeopleIcon from '@mui/icons-material/People';
import FilterListIcon from '@mui/icons-material/FilterList';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { pageTitleSx, filterCardSx } from '../styles/uiTokens';
import { brandTokens } from '../theme';

const MAX_RANGE_DAYS = 90;

const diffDays = (start, end) => {
  if (!start || !end) return 0;
  return Math.ceil((new Date(end) - new Date(start)) / (1000 * 60 * 60 * 24));
};

const getDefaultDataInicio = () => {
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  return yesterday.toISOString().slice(0, 10);
};

const getDefaultDataFim = () => {
  const today = new Date();
  return today.toISOString().slice(0, 10);
};

const MetricCard = ({ title, value, icon, color }) => {
  const theme = useTheme();
  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        height: '100%',
        borderRadius: 2,
        border: '1px solid',
        borderColor: alpha(color, 0.3),
        bgcolor: 'background.paper',
        boxShadow: `0 12px 32px ${brandTokens.shadow}`,
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: `0 16px 40px ${alpha(color, 0.15)}`
        }
      }}
    >
      <Box
        sx={{
          width: 56,
          height: 56,
          borderRadius: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: alpha(color, 0.12),
          color: color,
          flexShrink: 0
        }}
      >
        {icon}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
          {title}
        </Typography>
        <Typography variant="h3" sx={{ fontWeight: 900, color: 'text.primary', lineHeight: 1.2 }}>
          {value}
        </Typography>
      </Box>
    </Paper>
  );
};

function DashboardPage() {
  const theme = useTheme();
  const { user } = useAuth();
  const isAdminOuSindico = user?.tipo_usuario === 'admin' || user?.tipo_usuario === 'sindico';

  const [dataInicio, setDataInicio] = useState(getDefaultDataInicio);
  const [dataFim, setDataFim] = useState(getDefaultDataFim);
  const [selectedFuncionario, setSelectedFuncionario] = useState(null);
  const [funcionarioOptions, setFuncionarioOptions] = useState([]);
  const [loadingFuncionarios, setLoadingFuncionarios] = useState(false);
  const [dashboardData, setDashboardData] = useState({ acessos: 0, encomendas: 0, encomendasPendentes: 0, consumoAgua: 0 });
  const [loading, setLoading] = useState(false);

  const rangeDias = useMemo(() => diffDays(dataInicio, dataFim), [dataInicio, dataFim]);
  const rangeErro = useMemo(() => {
    if (!dataInicio || !dataFim) return 'Informe as datas de inicio e fim.';
    if (rangeDias < 0) return 'A data de inicio nao pode ser posterior a data fim.';
    if (rangeDias > MAX_RANGE_DAYS) return `O periodo maximo permitido e de ${MAX_RANGE_DAYS} dias (periodo atual: ${rangeDias} dias).`;
    return null;
  }, [dataInicio, dataFim, rangeDias]);

  const fetchDashboard = useCallback(() => {
    if (rangeErro) {
      setLoading(false);
      return;
    }
    setLoading(true);
    const params = { dataInicio, dataFim };
    if (isAdminOuSindico && selectedFuncionario?.pessoa_id) {
      params.funcionarioPessoaId = selectedFuncionario.pessoa_id;
    }
    api.get('/dashboard', { params })
      .then((response) => setDashboardData(response.data))
      .catch((error) => console.error('Erro ao buscar dados do dashboard:', error))
      .finally(() => setLoading(false));
  }, [dataInicio, dataFim, selectedFuncionario, isAdminOuSindico, rangeErro]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  useEffect(() => {
    if (!isAdminOuSindico) return;
    setLoadingFuncionarios(true);
    api.get('/funcionarios?page=0&limit=200')
      .then((response) => {
        const data = response.data?.data || response.data || [];
        const options = (Array.isArray(data) ? data : [])
          .filter((f) => f.pessoa_id && f.pessoa_nome)
          .map((f) => ({
            pessoa_id: f.pessoa_id,
            pessoa_nome: f.pessoa_nome
          }));
        setFuncionarioOptions(options);
      })
      .catch((error) => console.error('Erro ao buscar funcionários:', error))
      .finally(() => setLoadingFuncionarios(false));
  }, [isAdminOuSindico]);

  return (
    <Container sx={{ mt: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <Typography variant="h4" sx={pageTitleSx}>
          Painel
        </Typography>
        {loading && <CircularProgress size={20} />}
      </Box>

      <Paper elevation={0} sx={{ ...filterCardSx, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
          <FilterListIcon fontSize="small" color="primary" />
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            Filtros
          </Typography>
        </Box>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Data início"
              type="date"
              value={dataInicio}
              onChange={(e) => setDataInicio(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Data fim"
              type="date"
              value={dataFim}
              onChange={(e) => setDataFim(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              size="small"
            />
          </Grid>
          {isAdminOuSindico && (
            <Grid item xs={12} sm={6} md={4}>
              <Autocomplete
                size="small"
                options={funcionarioOptions}
                getOptionLabel={(option) => option.pessoa_nome || ''}
                isOptionEqualToValue={(option, value) => option.pessoa_id === value?.pessoa_id}
                value={selectedFuncionario}
                onChange={(event, newValue) => setSelectedFuncionario(newValue)}
                loading={loadingFuncionarios}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Funcionário"
                    placeholder="Todos"
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <PeopleIcon fontSize="small" color="action" sx={{ mr: 0.5 }} />
                          {params.InputProps.startAdornment}
                        </>
                      ),
                      endAdornment: (
                        <>
                          {loadingFuncionarios ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      )
                    }}
                  />
                )}
                fullWidth
              />
            </Grid>
          )}
          {rangeErro && (
            <Grid item xs={12}>
              <Alert severity="warning" variant="outlined" sx={{ borderRadius: 2 }}>
                {rangeErro}
              </Alert>
            </Grid>
          )}
        </Grid>
      </Paper>

      <Grid container spacing={2.5}>
        <Grid item xs={12} sm={6} lg={3}>
          <MetricCard
            title="Acessos"
            value={dashboardData.acessos}
            icon={<AccessTimeIcon sx={{ fontSize: 28 }} />}
            color={theme.palette.primary.main}
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <MetricCard
            title="Encomendas"
            value={dashboardData.encomendas}
            icon={<LocalShippingIcon sx={{ fontSize: 28 }} />}
            color="#e65100"
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <MetricCard
            title="Encomendas Pendentes"
            value={dashboardData.encomendasPendentes}
            icon={<HourglassEmptyIcon sx={{ fontSize: 28 }} />}
            color="#c62828"
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <MetricCard
            title="Leituras Hidrômetro"
            value={dashboardData.consumoAgua}
            icon={<WaterDropIcon sx={{ fontSize: 28 }} />}
            color="#00695c"
          />
        </Grid>
      </Grid>
    </Container>
  );
}

export default DashboardPage;
